import { ChangeEvent, forwardRef, Ref, RefAttributes, useCallback, useRef, type JSX } from "react";

import { Box, useTheme } from "@mui/material";
import { FieldValues, useController, useWatch } from "react-hook-form";
import PhoneInput, { CountryData } from "react-phone-input-2";

import { BaseFormPhoneInputFieldProps, FormPhoneInputFieldProps, SubscribedFormPhoneInputFieldProps } from "./types";

import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeRefs } from "@/helpers/refs";
import { useValidators } from "@/hooks/useValidators";

import "react-phone-input-2/lib/material.css";
import "react-phone-input-2/lib/style.css";

function FormPhoneInputFieldInner<T extends FieldValues>(
  { control, name, rules, ...rest }: FormPhoneInputFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  return control && name ? (
    <SubscribedFormPhoneInputField {...rest} ref={ref} control={control} name={name} rules={rules} />
  ) : (
    <BaseFormPhoneInputField {...rest} ref={ref} />
  );
}

FormPhoneInputFieldInner.displayName = "FormPhoneInputField";

export const FormPhoneInputField = forwardRef(FormPhoneInputFieldInner) as <T extends FieldValues>(
  props: FormPhoneInputFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

function SubscribedFormPhoneInputFieldInner<T extends FieldValues>(
  { control, name, rules, onBlur, onChange, inputRef, ...rest }: SubscribedFormPhoneInputFieldProps<T>,
  ref: Ref<HTMLInputElement>
) {
  const validators = useValidators(rules);
  const watchedValue = useWatch({ control, name });
  const { field } = useController<T>({
    name,
    control,
    defaultValue: watchedValue as never,
    rules: validators,
  });

  const handleChange = useCallback(
    (value: string, country: CountryData, event: ChangeEvent<HTMLInputElement>, formattedValue: string) => {
      onChange?.(value, country, event, formattedValue);
      field.onChange(value);
    },
    [field, onChange]
  );

  const handleBlur = useCallback(() => {
    onBlur?.();
    field.onBlur();
  }, [field, onBlur]);

  return (
    <BaseFormPhoneInputField
      {...rest}
      inputRef={inputRef}
      ref={ref}
      onBlur={handleBlur}
      onChange={handleChange}
      data-test={rest?.["data-test"] || "form-phone-input-field"}
      value={field.value}
    />
  );
}

const SubscribedFormPhoneInputField = forwardRef(SubscribedFormPhoneInputFieldInner) as <T extends FieldValues>(
  props: SubscribedFormPhoneInputFieldProps<T> & RefAttributes<HTMLInputElement>
) => JSX.Element;

const BaseFormPhoneInputField = forwardRef<HTMLInputElement, BaseFormPhoneInputFieldProps>(
  (
    {
      id,
      inputRef,
      placeholder,
      country = "us",
      value = "",
      onChange,
      error,
      disabled,
      autoFocus = false,
      "data-test": dataTest,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme();

    const innerInputRef = useRef<HTMLInputElement>(null);

    const handleOnChange = useCallback(
      (value: string, country: CountryData, event: ChangeEvent<HTMLInputElement>, formattedValue: string) => {
        innerInputRef.current?.focus();

        onChange?.(value, country, event, formattedValue);
      },
      [onChange]
    );

    if (
      !theme.palette.forms ||
      !theme.palette.formsComponents?.global ||
      !theme.palette.formsComponents?.textField ||
      !theme.palette.formsComponents?.selectField ||
      !theme.palette.formsComponents?.divider
    ) {
      return <InvalidThemeErrorMessage />;
    }

    const styles = {
      ...(disabled && {
        opacity: 0.4,
      }),
      ["& .form-input-field-container"]: {
        ["& .form-input-field-input"]: {
          width: "100%",
          height: 36,
          padding: theme.spacing("9.5px", 1.5, "9.5px", 6),
          ...theme.typography.body2,
          color: theme.palette.formsComponents.textField.textColor,
          backgroundColor: theme.palette.formsComponents.textField.backgroundColor,
          border: `1px solid ${theme.palette.formsComponents.textField.borderColor}`,
          borderRadius: theme.radius(1),
          outlineColor: "transparent",
          transition: "outline 100ms ease-out, border-color 200ms ease-out",
          ...(error && {
            borderColor: theme.palette.formsComponents.textField.errorBorderColor,
          }),
          ["&:disabled"]: {
            cursor: "default",
          },
          ["&::placeholder"]: {
            color: theme.palette.formsComponents.textField.placeholderTextColor,
          },
          ["&:focus:not(:disabled)"]: {
            borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
            outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
          },
          ["&:hover:not(:disabled)"]: {
            borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
          },
          ["&:hover:disabled"]: {
            borderColor: theme.palette.formsComponents.textField.borderColor,
          },
        },
        ["& .flag-dropdown"]: {
          backgroundColor: "transparent",
          border: "1px solid transparent",
          borderInlineEnd: `1px solid ${theme.palette.formsComponents.textField.borderColor}`,
          borderRadius: `${theme.radius(1)} 0 0 ${theme.radius(1)}`,
          outlineColor: "transparent",
          transition: "outline 100ms ease-out, border-color 200ms ease-out",
          ["&:hover"]: {
            borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
          },
          ["&.open"]: {
            backgroundColor: "transparent",
            borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
            borderRadius: `${theme.radius(1)} 0 0 ${theme.radius(1)}`,
            ["& .selected-flag"]: {
              backgroundColor: "transparent",
            },
            ["& .arrow"]: {
              rotate: "180deg",
            },
          },
          ["& .country-list .flag"]: {
            position: "static",
          },
          ...(!disabled && {
            ["&:has(:focus-visible)"]: {
              borderColor: theme.palette.formsComponents.textField.hoverBorderColor,
              outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
            },
          }),
        },
        ["& .selected-flag"]: {
          width: 40,
          ["&:hover, &:focus"]: {
            backgroundColor: "transparent",
          },
          ["& .arrow"]: {
            marginTop: "-3px",
            borderTop: `5px solid ${theme.palette.formsComponents.textField.textColor} !important`,
            borderLeftWidth: `4px !important`,
            borderRightWidth: `4px !important`,
            borderBottomWidth: `0px !important`,
            transition: "rotate 200ms ease-out",
          },
        },
        ["& .country-list"]: {
          backgroundColor: theme.palette.formsComponents.selectField.popoverBackgroundColor,
          color: theme.palette.formsComponents.selectField.itemTextColor,
          scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`, // We leave the track transparent to avoid issues with padding
          ["&::-webkit-scrollbar-track"]: {
            background: "transparent",
          },
          ["&::-webkit-scrollbar-thumb"]: {
            background: theme.palette.formsComponents.global.scrollbarThumbColor,
          },
          ["& .divider"]: {
            borderBottomColor: theme.palette.formsComponents.divider.borderColor,
          },
          ["& .country"]: {
            ...theme.typography.caption,
            color: theme.palette.formsComponents.selectField.itemTextColor,
            transition: "background-color 100ms linear, color 100ms linear",
            ["&:hover"]: {
              backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
            },
            ["&.active"]: {
              backgroundColor: theme.palette.formsComponents.selectField.selectedItemBackgroundColor,
              color: theme.palette.formsComponents.selectField.selectedItemTextColor,
              ["& .dial-code"]: {
                color: theme.palette.formsComponents.selectField.selectedItemTextColor,
              },
            },
            ["&.highlight"]: {
              backgroundColor: theme.palette.formsComponents.selectField.itemHoverBackgroundColor,
              color: theme.palette.formsComponents.selectField.itemTextColor,
              ["& .dial-code"]: {
                color: theme.palette.formsComponents.selectField.itemTextColor,
              },
            },
            ["& .dial-code"]: {
              color: theme.palette.formsComponents.selectField.itemTextColor,
              opacity: 0.7,
            },
          },
        },
      },
    };

    return (
      <FormField ref={ref} id={id} error={error} {...getFormFieldProps(rest)} data-test={dataTest}>
        <Box sx={styles}>
          <PhoneInput
            key={country}
            country={country}
            value={value}
            containerClass="form-input-field-container"
            inputClass="form-input-field-input"
            specialLabel=""
            preferredCountries={["us", "ca"]}
            inputProps={{
              ref: mergeRefs(inputRef, innerInputRef),
              placeholder,
              autoFocus,
              id,
              disableAutocomplete: true,
            }}
            disabled={disabled}
            disableDropdown={disabled}
            onChange={handleOnChange}
            {...rest}
          />
        </Box>
      </FormField>
    );
  }
);

BaseFormPhoneInputField.displayName = "BaseFormPhoneInputField";
